import React, { useState, useEffect, useRef, useCallback } from 'react';
import { addLocale } from 'primereact/api';
import { useNavigate } from 'react-router-dom';
import classNames from 'classnames';
import axios from 'axios';
import * as XLSX from 'xlsx';

import { format } from "date-fns";
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Toast } from 'primereact/toast';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Dialog } from 'primereact/dialog';
import { InputSwitch } from 'primereact/inputswitch';
import { Tag } from 'primereact/tag';
import { Calendar } from 'primereact/calendar';
import { Toolbar } from 'primereact/toolbar';
import { Tooltip } from 'primereact/tooltip';
import { PersonaEntity } from '../entity/PersonaEntity';
import { useAuth } from '../context/AuthContext';
import { FileUpload } from 'primereact/fileupload';

addLocale('es', {
    firstDayOfWeek: 1,
    dayNames: ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'],
    dayNamesShort: ['dom', 'lun', 'mar', 'mié', 'jue', 'vie', 'sáb'],
    dayNamesMin: ['D', 'L', 'M', 'X', 'J', 'V', 'S'],
    monthNames: ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'],
    monthNamesShort: ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'],
    today: 'Hoy',
    clear: 'Limpiar',
});

export const Personas = () => {
    const { user, axiosInstance } = useAuth();
    const navigate = useNavigate();

    const colors = {
        primary: '#0B396E',
        secondary: '#CFB53B',
        accent: '#800000',
        text: '#444444',
        background: '#f8f9fa',
        cardBg: '#ffffff',
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        if (dateString instanceof Date) {
            return format(dateString, 'dd/MM/yyyy');
        }
        if (typeof dateString !== 'string') return '-';

        const [year, month, day] = dateString.split('-');

        if (!year || !month || !day) return '-';

        const date = new Date(year, month - 1, day);
        return format(date, 'dd/MM/yyyy');
    };

    const stringToDate = (dateString) => {
        if (!dateString) return null;
        if (dateString instanceof Date) {
            return dateString;
        }
        if (typeof dateString === 'number') {
            return new Date(dateString);
        }
        if (typeof dateString === 'string' && dateString.includes('/')) {
            const [day, month, year] = dateString.split('/').map(Number);
            return new Date(year, month - 1, day);
        }
        return null;
    };

    const empty = PersonaEntity;
    const [data, setData] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [loading, setLoading] = useState(false);
    const [lazyParams, setLazyParams] = useState({
        first: 0,
        rows: 10,
        page: 0
    });
    const [EntidadNewDialog, setEntidadNewDialog] = useState(false);
    const [product, setProduct] = useState(empty);
    const [submitted, setSubmitted] = useState(false);
    const [globalFilter, setGlobalFilter] = useState(null);
    const [bulkUploadDialog, setBulkUploadDialog] = useState(false);

    const [isSaving, setIsSaving] = useState(false); // Para control de doble envío
    const [isSearchingRENIEC, setIsSearchingRENIEC] = useState(false); // Para feedback de la API externa

    const baseUrl = 'persona/';

    const toast = useRef(null);
    const dt = useRef(null);

    const peticionGet = useCallback(async () => {
        setLoading(true);
        try {
            const page = lazyParams.first / lazyParams.rows;
            const response = await axiosInstance.get('/persona/', {
                params: {
                    page,
                    size: lazyParams.rows,
                    search: globalFilter || ''
                }
            });
            setData(response.data.content);
            setTotalRecords(response.data.totalElements);
        } catch (error) {
            console.error(error);
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'No se pudieron cargar los datos de personas.', life: 5000 });
        } finally {
            setLoading(false);
        }
    }, [axiosInstance, lazyParams, globalFilter]);

    const peticionRENIEC = useCallback(async () => {
        if (!product.docIdentidad) {
            toast.current.show({ severity: 'warn', summary: 'Advertencia', detail: 'Ingrese un DNI para buscar.', life: 3000 });
            return;
        }

        setIsSearchingRENIEC(true);

        try {
            const response = await axios.get("https://dniruc.apisperu.com/api/v1/dni/" + product.docIdentidad + "?token=eyJ0eXAiOiJKV1QiLCJhbGciOiJIUzI1NiJ9.eyJlbWFpbCI6ImpnYWl0YW5yQHBqLmdvYi5wZSJ9.-nX87AiyjDvfW2SeGAhWFnx0MDCiB8meK06aAAlVfJQ");
            const { nombres, apellidoPaterno, apellidoMaterno } = response.data;
            setProduct((prevProduct) => ({
                ...prevProduct,
                nombre: nombres,
                apellido: `${apellidoPaterno} ${apellidoMaterno}`,
            }));
            toast.current.show({ severity: 'success', summary: 'Éxito', detail: 'Datos obtenidos de RENIEC.', life: 3000 });
        } catch (error) {
            console.error("Error al consultar RENIEC:", error);
            const detail = error.response?.status === 404 ? 'DNI no encontrado o error en la consulta.' : 'No se pudieron obtener datos de RENIEC.';
            toast.current.show({ severity: 'error', summary: 'Error', detail: detail, life: 5000 });
        } finally {
            setIsSearchingRENIEC(false);
        }
    }, [product.docIdentidad, toast]);

    const peticionPost = async () => {
        setIsSaving(true);
        try {
            const newProduct = { ...product };
            delete newProduct.id;
            const response = await axiosInstance.post('/persona/', newProduct);
            peticionGet();
            toast.current.show({ severity: 'success', summary: 'Éxito', detail: 'Registro creado correctamente.', life: 3000 });
            setEntidadNewDialog(false);
        } catch (error) {
            console.error(error);
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'Datos incorrectos o error al registrar.', life: 5000 });
        } finally {
            setIsSaving(false);
        }
    };

    const peticionPut = async () => {
        setIsSaving(true);
        try {
            await axiosInstance.put('/persona/', product);
            setData(prevData => prevData.map(u => u.id === product.id ? product : u));
            toast.current.show({ severity: 'success', summary: 'Éxito', detail: 'Registro modificado correctamente.', life: 3000 });
            setEntidadNewDialog(false);
        } catch (error) {
            console.error(error);
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'Datos incorrectos o error al modificar.', life: 5000 });
        } finally {
            setIsSaving(false);
        }
    };

    const handleBulkUpload = async (event) => {
        const file = event.files[0];
        const formData = new FormData();
        formData.append('file', file);
        const endpoint = `${baseUrl}cargar-masiva`;

        try {
            const response = await axiosInstance.post(endpoint, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });

            const cargaMasivaResponse = response.data;
            const { totalRegistros, registrosNuevos, registrosActualizados, registrosFallidos, errores } = cargaMasivaResponse;

            const summaryDetail = `Total: ${totalRegistros}. Nuevos: ${registrosNuevos}. Actualizados: ${registrosActualizados}. Fallidos: ${registrosFallidos}.`;

            if (registrosFallidos === 0) {
                toast.current.show({
                    severity: 'success',
                    summary: 'Carga Masiva Exitosa',
                    detail: summaryDetail,
                    life: 5000
                });
            } else if (registrosNuevos + registrosActualizados > 0) {
                toast.current.show({
                    severity: 'warn',
                    summary: 'Carga Parcial con Errores',
                    detail: `${summaryDetail} Exportando lista de errores...`,
                    life: 10000
                });
                exportarErroresAExcel(errores);

            } else if (totalRegistros > 0 && registrosFallidos === totalRegistros) {
                toast.current.show({
                    severity: 'error',
                    summary: 'Fallo Total de Carga',
                    detail: `${summaryDetail} Se generó un archivo de errores.`,
                    life: 10000
                });
                exportarErroresAExcel(errores);

            } else {
                toast.current.show({ severity: 'info', summary: 'Proceso Finalizado', detail: 'El archivo estaba vacío o no se detectaron registros válidos.', life: 5000 });
            }

            setBulkUploadDialog(false);
            peticionGet();

        } catch (error) {
            const errorData = error.response?.data;
            let errorMessage = 'Error desconocido al procesar el archivo o de conexión.';
            if (errorData && errorData.errores && errorData.errores.length > 0) {
                errorMessage = `Error fatal de archivo: ${errorData.errores[0]}`;
            } else {
                errorMessage = error.response?.data?.message || error.message || 'Error de conexión o servidor.';
            }
            toast.current.show({ severity: 'error', summary: 'Error de Carga', detail: errorMessage, life: 5000 });
        }
    };

    const onPage = (event) => {
        setLazyParams((prevState) => ({
            ...prevState,
            first: event.first,
            rows: event.rows
        }));
    };

    useEffect(() => {
        peticionGet();
    }, [lazyParams, globalFilter, peticionGet]);

    const handleDownloadTemplate = () => {
        const link = document.createElement('a');
        link.href = '/formato/Formato_Personas.xlsx';
        link.download = 'plantilla-personas.xlsx';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const exportarErroresAExcel = (errores) => {
        if (!errores || errores.length === 0) return;

        const datosParaExportar = errores.map((errorString, index) => ({
            '#': index + 1,
            'Detalle del Error (Fila, DNI y Causa Incluidos)': errorString,
        }));

        const ws = XLSX.utils.json_to_sheet(datosParaExportar);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Errores de Carga");
        // Generar un nombre de archivo único con fecha y hora
        const today = format(new Date(), 'yyyyMMdd_HHmmss');
        const filename = `Errores_Personas_CargaMasiva_${today}.xlsx`;

        try {
            XLSX.writeFile(wb, filename);
            toast.current.show({ severity: 'info', summary: 'Exportación Completa', detail: 'El archivo de errores se ha descargado.', life: 3000 });
        } catch (e) {
            toast.current.show({ severity: 'error', summary: 'Error de Exportación', detail: 'No se pudo generar el archivo Excel de errores.', life: 3000 });
            console.error("Error al escribir el archivo XLSX:", e);
        }
    };

    const openNew = () => {
        setProduct(empty);
        setSubmitted(false);
        setEntidadNewDialog(true);
    };

    const hideDialogNew = () => {
        setSubmitted(false);
        setEntidadNewDialog(false);
    };

    const editProduct = (product) => {
        setProduct({
            ...product,
            fecNacimiento: product.fecNacimiento ? stringToDate(product.fecNacimiento) : null
        });
        setEntidadNewDialog(true);
    };

    const saveProduct = () => {
        setSubmitted(true);

        const requiredFields = [
            'docIdentidad',
            'nombre',
            'apellido'
        ];

        const isValid = requiredFields.every(field => product[field] && product[field].toString().trim());

        if (isValid) {
            const productToSend = { ...product };
            if (productToSend.fecNacimiento instanceof Date) {
                productToSend.fecNacimiento = format(productToSend.fecNacimiento, 'yyyy-MM-dd');
            }

            setProduct(productToSend);

            if (productToSend.id) {
                peticionPut();
            } else {
                peticionPost();
            }
        } else {
            toast.current.show({
                severity: 'error',
                summary: 'Datos Incompletos',
                detail: 'Por favor, complete todos los campos requeridos (*).',
                life: 3000
            });
        }
    };

    const exportCSV = () => {
        dt.current.exportCSV();
    };

    const BuscarDNI = () => {
        setSubmitted(true);
        if (!isSearchingRENIEC && product.docIdentidad && product.docIdentidad.trim()) {
            peticionRENIEC();
        } else if (!product.docIdentidad || !product.docIdentidad.trim()) {
            toast.current.show({ severity: 'warn', summary: 'Advertencia', detail: 'Ingrese un DNI válido para buscar.', life: 3000 });
        }
    };

    const onInputChange = (e, name) => {
        const val = (e.target && e.target.value) || e.value;
        setProduct(prevProduct => ({
            ...prevProduct,
            [name]: val
        }));
    };

    const handleSwitchChange = (event) => {
        setProduct({ ...product, estado: event.value });
    };

    const leftToolbarTemplate = () => {
        return (
            <React.Fragment>
                <div className="my-2">
                    <Button
                        label="Registrar"
                        icon="pi pi-user-plus"
                        style={{ backgroundColor: colors.accent, color: colors.cardBg, borderColor: colors.accent }}
                        className="p-button-outlined mr-2"
                        onClick={openNew}
                    />
                    <Button
                        label="Carga Masiva"
                        icon="pi pi-cloud-upload"
                        style={{ backgroundColor: colors.primary, color: colors.cardBg, borderColor: colors.primary }}
                        className="p-button-outlined"
                        onClick={() => setBulkUploadDialog(true)}
                    />
                </div>
            </React.Fragment>
        );
    };

    const rightToolbarTemplate = () => {
        return (
            <React.Fragment>
                <Button
                    label="Exportar"
                    icon="pi pi-upload"
                    style={{ backgroundColor: colors.accent, color: colors.cardBg, borderColor: colors.accent }}
                    className="p-button-outlined"
                    onClick={exportCSV}
                />
            </React.Fragment>
        );
    };

    const docIdentidadBodyTemplate = (rowData) => <span style={{ color: colors.primary }}>{rowData.docIdentidad || '-'}</span>;
    const escalafonBodyTemplate = (rowData) => rowData.escalafon || '-';
    const nombreBodyTemplate = (rowData) => <span style={{ fontWeight: 'bold' }}>{rowData.nombre} {rowData.apellido}</span> || '-';
    const fecNacimientoBodyTemplate = (rowData) => formatDate(rowData.fecNacimiento) || '-';

    const estadoBodyTemplate = (rowData) => {
        let severity;
        let info;

        if (rowData.estado) {
            severity = "success";
            info = "Activo";
        } else {
            severity = "warning";
            info = "Inactivo";
        }
        return <Tag value={info} severity={severity} />;
    };

    const actionBodyTemplate = (rowData) => {
        return (
            <div className="actions">
                <Button
                    title="Modificar"
                    icon="pi pi-pencil"
                    className="p-button-rounded p-button-text p-2 rounded-full"
                    onClick={() => editProduct(rowData)}
                    style={{ color: colors.accent }}
                />
            </div>
        );
    };

    const header = (
        <div className="flex flex-column md:flex-row md:justify-content-between md:align-items-center">
            <h5 className="m-0" style={{ color: colors.primary }}>Lista de Servidores</h5>
            <span className="block mt-2 md:mt-0 p-input-icon-left">
                {/*<i className="pi pi-search" />*/}
                <InputText
                    type="search"
                    value={globalFilter || ''}
                    onChange={(e) => setGlobalFilter(e.target.value)}
                    placeholder="Buscar..."
                />
            </span>
        </div>
    );

    const productDialogFooterNew = (
        <>
            <Button label="Cancelar" icon="pi pi-times" className="p-button-text" onClick={hideDialogNew} disabled={isSaving} />
            <Button label="Aceptar" icon="pi pi-check" className="p-button-text" onClick={saveProduct} loading={isSaving} disabled={isSaving} />
        </>
    );

    return (
        <div className="grid">
            <div className="col-12">
                <div className="card">
                    <Toast ref={toast} />
                    <Tooltip target=".search-dni-btn" content="Consultar DNI en RENIEC" position="bottom" />
                    <Toolbar className="mb-4" left={leftToolbarTemplate} right={rightToolbarTemplate}></Toolbar>
                    <DataTable ref={dt} value={data} dataKey="id"
                        paginator className="datatable-responsive"
                        lazy
                        first={lazyParams.first}
                        rows={lazyParams.rows}
                        totalRecords={totalRecords}
                        onPage={onPage}
                        loading={loading}
                        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                        currentPageReportTemplate="Mostrando {first} a {last} de {totalRecords} Servidores"
                        emptyMessage="No existen registros." header={header}>
                        <Column field="docIdentidad" header="Documento" style={{ minWidth: '8rem', color: colors.primary }} body={docIdentidadBodyTemplate}></Column>
                        <Column field="escalafon" header="Escalafon" style={{ minWidth: '8rem' }} body={escalafonBodyTemplate}></Column>
                        <Column field="nombre" header="Nombre Completo" style={{ minWidth: '18rem' }} body={nombreBodyTemplate}></Column>
                        <Column field="fecNacimiento" header="Fec. Nacimiento" style={{ minWidth: '10rem' }} body={fecNacimientoBodyTemplate}></Column>
                        <Column field="estado" header="Estado" style={{ minWidth: '8rem' }} body={estadoBodyTemplate}></Column>
                        <Column body={actionBodyTemplate} style={{ minWidth: '8rem' }}></Column>
                    </DataTable>

                    <Dialog
                        visible={EntidadNewDialog}
                        style={{ width: '800px' }}
                        header={product.id ? 'Modificar Servidor' : 'Registrar Servidor'}
                        modal className="p-fluid"
                        footer={productDialogFooterNew}
                        onHide={hideDialogNew}>

                        <div className="field col-12">
                            <div className="formgrid grid">
                                <div className="field col-12 md:col-6">
                                    <label htmlFor="activo">Activar Servidor</label>
                                    <br />
                                    <InputSwitch id="activo" name="activo" checked={product.estado} onChange={handleSwitchChange} disabled={isSaving} />
                                </div>
                            </div>
                            <div className="formgrid grid">
                                <div className="field col-12 md:col-6">
                                    <label htmlFor="docIdentidad">Doc. de Identidad <span className="p-error">*</span></label>
                                    <div className="p-inputgroup">
                                        <Button
                                            icon={isSearchingRENIEC ? "pi pi-spin pi-spinner" : "pi pi-search"}
                                            onClick={BuscarDNI}
                                            disabled={isSearchingRENIEC || isSaving || !product.docIdentidad || product.docIdentidad.length !== 8}
                                            className="search-dni-btn"
                                        />
                                        <InputText
                                            id="docIdentidad"
                                            name="docIdentidad"
                                            value={product.docIdentidad}
                                            onChange={(e) => onInputChange(e, 'docIdentidad')}
                                            required
                                            disabled={isSearchingRENIEC || isSaving}
                                            className={classNames({ 'p-invalid': submitted && !product.docIdentidad })}
                                            maxLength={8}
                                        />
                                    </div>
                                    {submitted && !product.docIdentidad && <small className="p-invalid">El documento de identidad es requerido.</small>}
                                </div>
                                <div className="field col-12 md:col-6">
                                    <label htmlFor="escalafon">Escalafon <span className="p-error">*</span></label>
                                    <InputText id="escalafon" name="escalafon" value={product.escalafon} onChange={(e) => onInputChange(e, 'escalafon')} required disabled={isSaving} className={classNames({ 'p-invalid': submitted && !product.escalafon })} />
                                    {submitted && !product.escalafon && <small className="p-invalid">El escalafon es requerido.</small>}
                                </div>
                            </div>
                            <div className="formgrid grid">
                                <div className="field col-12 md:col-6">
                                    <label htmlFor="nombre">Nombres <span className="p-error">*</span></label>
                                    <InputText id="nombre" name="nombre" value={product.nombre} onChange={(e) => onInputChange(e, 'nombre')} required disabled={isSaving} className={classNames({ 'p-invalid': submitted && !product.nombre })} />
                                    {submitted && !product.nombre && <small className="p-invalid">El Nombre es requerido.</small>}
                                </div>
                                <div className="field col-12 md:col-6">
                                    <label htmlFor="apellido">Apellidos <span className="p-error">*</span></label>
                                    <InputText id="apellido" name="apellido" value={product.apellido} onChange={(e) => onInputChange(e, 'apellido')} required disabled={isSaving} className={classNames({ 'p-invalid': submitted && !product.apellido })} />
                                    {submitted && !product.apellido && <small className="p-invalid">El Apellido es requerido.</small>}
                                </div>
                            </div>
                            <div className="formgrid grid">
                                <div className="field col-12 md:col-6">
                                    <label htmlFor="fecNacimiento">Fecha de Nacimiento</label>
                                    <Calendar
                                        id="fecNacimiento"
                                        name="fecNacimiento"
                                        placeholder="dd/mm/aaaa"
                                        value={product.fecNacimiento ? stringToDate(product.fecNacimiento) : null}
                                        dateFormat="dd/mm/yy"
                                        locale='es'
                                        onChange={(e) => onInputChange(e, 'fecNacimiento')}
                                        disabled={isSaving}
                                    />
                                </div>
                            </div>
                            <div className="formgrid grid">
                                <div className="field col-12 md:col-6">
                                    <label htmlFor="email">Correo <span className="p-error">*</span></label>
                                    <InputText id="email" name="email" value={product.email} onChange={(e) => onInputChange(e, 'email')} required disabled={isSaving} className={classNames({ 'p-invalid': submitted && !product.email })} />
                                    {submitted && !product.email && <small className="p-invalid">El Correo es requerido.</small>}
                                </div>
                                <div className="field col-12 md:col-6">
                                    <label htmlFor="nroTelefono">Teléfono <span className="p-error">*</span></label>
                                    <InputText id="nroTelefono" name="nroTelefono" value={product.nroTelefono} onChange={(e) => onInputChange(e, 'nroTelefono')} required disabled={isSaving} className={classNames({ 'p-invalid': submitted && !product.nroTelefono })} />
                                    {submitted && !product.nroTelefono && <small className="p-invalid">El Teléfono es requerido.</small>}
                                </div>
                            </div>
                            <div className="formgrid grid">
                                <div className="field col-12">
                                    <label htmlFor="direccion">Dirección <span className="p-error">*</span></label>
                                    <InputText id="direccion" name="direccion" value={product.direccion} onChange={(e) => onInputChange(e, 'direccion')} required disabled={isSaving} className={classNames({ 'p-invalid': submitted && !product.direccion })} />
                                    {submitted && !product.direccion && <small className="p-invalid">La dirección es requerida.</small>}
                                </div>
                            </div>
                        </div>
                    </Dialog>

                    {/* DIÁLOGO DE CARGA MASIVA - Se mantiene igual, pero se puede añadir un spinner para la carga */}
                    <Dialog visible={bulkUploadDialog} style={{ width: '52vw' }} header="Carga Masiva de Personas" modal onHide={() => setBulkUploadDialog(false)}>
                        <div className="p-fluid grid formgrid">
                            <div className="col-12">
                                <p className="text-sm text-600 mb-2">Utilice esta herramienta para registrar/actualizar múltiples personas de forma eficiente.</p>
                            </div>
                            <div className="col-12 md:col-6 flex flex-column justify-content-between p-4 border-right-1 surface-border h-full">
                                <p className="font-bold">1. Descargar la plantilla</p>
                                <p className="text-sm">Para evitar errores, use nuestra plantilla oficial de Excel con el formato correcto. Las columnas esperadas son: **docIdentidad, escalafon, nombre, apellido, fecNacimiento, direccion, email, nroTelefono**.</p>
                                <Button
                                    label="Descargar Plantilla"
                                    icon="pi pi-download"
                                    className="p-button-outlined p-button-secondary mt-auto"
                                    onClick={handleDownloadTemplate} />
                            </div>
                            <div className="col-12 md:col-6 flex flex-column justify-content-between p-4">
                                <p className="font-bold">2. Subir el archivo</p>
                                <p className="text-sm">Seleccione o arrastre aquí el archivo .xlsx con la plantilla llena.</p>
                                <FileUpload
                                    name="file"
                                    //url={`${baseUrl}/cargar-masiva`}
                                    accept=".xlsx"
                                    maxFileSize={20000000}
                                    customUpload
                                    uploadHandler={handleBulkUpload}
                                    chooseLabel="Elegir Archivo"
                                    chooseOptions={{
                                        icon: 'pi pi-fw pi-file-excel',
                                        label: 'Elegir Archivo',
                                        style: {
                                            backgroundColor: colors.primary,
                                            borderColor: colors.primary,
                                            color: 'white'
                                        },
                                        className: 'p-button-sm p-button-raised'
                                    }}
                                    uploadLabel="Cargar Datos"
                                    uploadOptions={{
                                        icon: 'pi pi-fw pi-upload',
                                        label: 'Cargar Datos',
                                        style: {
                                            backgroundColor: colors.accent,
                                            borderColor: colors.accent,
                                            color: 'white'
                                        },
                                        className: 'p-button-sm p-button-raised'
                                    }}
                                    cancelLabel="Eliminar"
                                    cancelOptions={{
                                        icon: 'pi pi-fw pi-times',
                                        label: 'Eliminar',
                                        style: {
                                            backgroundColor: 'transparent',
                                            borderColor: colors.accent,
                                            color: colors.accent
                                        },
                                        className: 'p-button-sm p-button-text'
                                    }}
                                    className="p-button-sm p-button-raised"
                                    emptyTemplate={
                                        <div className="flex flex-column align-items-center justify-content-center p-3 border-2 border-dashed border-round surface-border">
                                            <i className="pi pi-file-excel text-5xl text-400 mb-3" />
                                            <p className="m-0 text-700">Arrastra y suelta tu archivo aquí</p>
                                            <p className="m-0 text-500 text-sm">O haz clic para explorar</p>
                                        </div>
                                    }
                                />
                            </div>
                        </div>
                    </Dialog>
                </div>
            </div>
        </div>
    );
}

export default Personas;